import { Theme } from '@simplywallst/ui-core';
import { SNOWFLAKE_WIDTH_RATIO } from '../constants';
import { FLAKE_MAX_COLOUR, FLAKE_MIN_COLOUR } from './getSnowflakeColors';
import { calculateMidColor } from './colors';

const color: Pick<Theme['color'], 'white' | 'spaceGray' | 'darkerGray'> = {
  white: '#FFFFFF',
  spaceGray: '#262E3A',
  darkerGray: '#3E4855',
};

// Calculates the bezier control points for segment (x1,y1)-(x2,y2)
const calculateControlPoints = (
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  tensor: number
) => {
  const len1 = Math.sqrt((x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0));
  const len2 = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
  const len3 = Math.sqrt((x3 - x2) * (x3 - x2) + (y3 - y2) * (y3 - y2));

  const k1 = len1 / (len1 + len2);
  const k2 = len2 / (len2 + len3);

  const xc1 = (x0 + x1) / 2.0;
  const yc1 = (y0 + y1) / 2.0;
  const xc2 = (x1 + x2) / 2.0;
  const yc2 = (y1 + y2) / 2.0;
  const xc3 = (x2 + x3) / 2.0;
  const yc3 = (y2 + y3) / 2.0;

  const xm1 = xc1 + (xc2 - xc1) * k1;
  const ym1 = yc1 + (yc2 - yc1) * k1;

  const xm2 = xc2 + (xc3 - xc2) * k2;
  const ym2 = yc2 + (yc3 - yc2) * k2;

  const cx1 = xm1 + (xc2 - xm1) * tensor + x1 - xm1;
  const cy1 = ym1 + (yc2 - ym1) * tensor + y1 - ym1;

  const cx2 = xm2 + (xc2 - xm2) * tensor + x2 - xm2;
  const cy2 = ym2 + (yc2 - ym2) * tensor + y2 - ym2;

  return [cx1, cy1, cx2, cy2];
};

type Args = {
  ctx: CanvasRenderingContext2D;
  points: number[];
  styleType?: 'light' | 'dark';
  blobFillStyle?: string;
  blobStrokeStyle?: string;
};

interface Point {
  x: number;
  y: number;
}

export const paintSnowflakeToCanvas = ({
  ctx,
  points,
  styleType,
  blobFillStyle,
  blobStrokeStyle,
}: Args) => {
  const width = ctx.canvas.width;
  const height = ctx.canvas.width;
  const axisLineWidth = 3 / 280;
  const PI = Math.PI;
  const diameter = width * SNOWFLAKE_WIDTH_RATIO;
  const arr: Point[] = [];
  const nodeCount = points.length;
  const maxSum = 30;
  const minSum = 5;
  const sum: number = points.reduce((a, b) => +a + (+b - 1), 0);
  const colour = 1 - (sum - minSum) / (maxSum - minSum);
  // null check;
  if (nodeCount === 0) return;
  let max = 7;
  let flakeColorValue = 0;

  flakeColorValue = Math.max(colour, 0);
  flakeColorValue = Math.min(colour, 1);

  for (let i = 0; i < nodeCount; i++) {
    max = Math.max(max, points[i]);
  }

  for (let i = 0; i < nodeCount; i++) {
    const radius = ((diameter / 2) * points[i]) / max;
    const angle = -PI / 2 + ((2 * PI) / nodeCount) * i;
    const x = width / 2 + Math.cos(angle) * radius;
    const y = width / 2 + Math.sin(angle) * radius;
    arr.push({ x: x, y: y });
  }
  ctx.clearRect(0, 0, width, height);
  ctx.translate(0, 0);

  // Draw bullseye arcs
  for (let i = max; i > 1; i--) {
    ctx.beginPath();

    const isEven = i % 2 !== 0;
    if (styleType === 'light') {
      ctx.fillStyle = isEven ? color.spaceGray : color.white;
    } else {
      ctx.fillStyle = isEven ? color.spaceGray : color.darkerGray;
    }
    ctx.arc(width / 2, height / 2, (i / max) * (diameter / 2), 0, 2 * PI);
    ctx.fill();
  }
  // Draw axis lines
  for (let i = 5; i > 0; i--) {
    const radius = diameter / 2;
    const angle = -PI / 2 + ((2 * PI) / nodeCount) * i;
    const x = width / 2 + Math.cos(angle) * radius;
    const y = width / 2 + Math.sin(angle) * radius;
    ctx.beginPath();
    ctx.strokeStyle = styleType === 'light' ? color.white : color.darkerGray;
    ctx.lineWidth = width * axisLineWidth;
    ctx.moveTo(width / 2, height / 2);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  // Draw blob
  ctx.beginPath();
  ctx.strokeStyle =
    blobStrokeStyle ||
    calculateMidColor(FLAKE_MAX_COLOUR, FLAKE_MIN_COLOUR, flakeColorValue, 1);
  ctx.fillStyle =
    blobFillStyle ||
    calculateMidColor(FLAKE_MAX_COLOUR, FLAKE_MIN_COLOUR, flakeColorValue, 0.7);
  ctx.lineWidth = 4;
  ctx.moveTo(arr[0].x, arr[0].y);

  for (let i = 0; i < nodeCount; i++) {
    const x0 = arr[(i - 1 + nodeCount) % nodeCount].x;
    const y0 = arr[(i - 1 + nodeCount) % nodeCount].y;
    const x1 = arr[i].x;
    const y1 = arr[i].y;
    const x2 = arr[(i + 1) % nodeCount].x;
    const y2 = arr[(i + 1) % nodeCount].y;
    const x3 = arr[(i + 2) % nodeCount].x;
    const y3 = arr[(i + 2) % nodeCount].y;
    const bezier = calculateControlPoints(x0, y0, x1, y1, x2, y2, x3, y3, 1);
    ctx.bezierCurveTo(
      bezier[0],
      bezier[1],
      bezier[2],
      bezier[3],
      arr[(i + 1) % nodeCount].x,
      arr[(i + 1) % nodeCount].y
    );
  }
  ctx.fill();
  ctx.stroke();

  ctx.save();
  ctx.restore();
};
